"use client";

/**
 * useReadingProgress
 * @description 대상 요소를 얼마나 읽었나(0~1). 스크롤·리사이즈마다 한 프레임에 한 번만 잰다.
 *   대상의 위가 뷰포트 위에 닿는 순간이 0, 아래가 뷰포트 아래에 닿는 순간이 1 — 글보다 짧은
 *   화면에서 "다 읽었다" 가 마지막 줄이 보일 때와 같아진다.
 * @example
 * const { fillRef, percent } = useReadingProgress("post-article");
 */
import { useEffect, useRef, useState, type RefObject } from "react";

const PERCENT = 100;

export function useReadingProgress(targetId: string): {
  fillRef: RefObject<HTMLDivElement | null>;
  percent: number;
} {
  const fillRef = useRef<HTMLDivElement>(null);
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target) return;
    let frame = 0;

    const measure = () => {
      frame = 0;
      const rect = target.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      const progress =
        scrollable <= 0 ? 1 : Math.min(1, Math.max(0, -rect.top / scrollable));
      if (fillRef.current)
        fillRef.current.style.transform = `scaleX(${progress})`;
      /* 읽어주는 값은 정수 퍼센트가 바뀔 때만 — 매 프레임 렌더를 돌리지 않는다 */
      setPercent((prev) => {
        const next = Math.round(progress * PERCENT);
        return next === prev ? prev : next;
      });
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [targetId]);

  return { fillRef, percent };
}
